import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "@cores/loadable";
import compareVersions, {
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_15524_NOTIFY_POPPER,
    VERSION_CM_13540_ASSET_SEARCH_BAR_V2,
} from "@cores/version";
import { useTranslation } from "react-i18next";
import { CONSTANTS, SIDEBAR_BUTTON_ID } from "@constants";
import { isDev, isLocal, isQA } from "@cores/enviromentExtension";
import SelectStage from "@components/navigator/components/SelectStage";
import Logo from "./Logo";
import StartMenu from "../../features/start-menu/start-menu";
import ProfileMenu from "../../features/profile-menu/profile-menu";
import menuService from "@services/menuService";
import theme from "@styles/theme";
import {
    AppBar,
    Button,
    Collapse,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Toolbar,
    Tooltip,
} from "@mzc-pdc/ui";
import {
    IconClose,
    IconForward,
    IconConsoleRequest,
    IconReply,
    IconSearch,
    IconShared,
} from "@mzc-cloudplex/icons";
import { alpha } from "@mui/material";
import queryParamsParse from "../params/queryParamsParse";
import { toggleActive } from "@modules/rightSideBar";
import useSettings from "@hooks/useSettings";
import useCheckPermission from "@hooks/useCheckPermission";
import useHeaderTheme from "@hooks/useHeaderTheme";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { siteMode } from "@cores/siteMode";
import { NotifyButton } from "@features/notify-popper";
import VersionedComponent from "@components/VersionedComponent";
import { safeJsonParse } from "@utils/safeJsonParse";
import MyJobsButton from "@components_v2/my-jobs-button";

const LoadableModelForAppDown = Loadable({
    loader: () => import("@components/modals/AppDownload"),
    loading: () => null,
});

const HeaderForLayout = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { pathname, search } = location;
    const { spaceId, projectId } = menuService.parse(pathname);
    const isLibrary = siteMode.get() === "CMS";

    const COMMENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Comments" });
    const INTEGRATED_SEARCH_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "IntegratedSearch" });
    const { domainStatus } = useSettings({ domain: "project", type: "DomainStatus" });
    const { getSettingData, resourceSettings } = useSettings({
        domain: "assets",
    });
    const user = useSelector(({ user }) => user);
    const stage = useSelector(({ stage }) => stage);
    const settings = useSelector(({ settings }) => settings);
    const rightSideBar = useSelector(({ rightSideBar }) => rightSideBar);
    const project = useSelector(({ project }) => project);

    const { headerTheme, logoSrc } = useHeaderTheme({ projectId });

    const [activeAppDownModal, setActiveAppDownModal] = useState(false);
    const [isSearch, setIsSearch] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState(``);

    const closeAppDownModal = () => {
        setActiveAppDownModal(false);
    };

    const onSubmit = () => {
        setIsSearch(false);
        const listConditionValue = resourceSettings.settings?.Asset?.listCondition?.value;
        const listCondition = listConditionValue ? safeJsonParse(listConditionValue) : null;
        const isNotIngestTypeParam =
            listCondition?.isNotIngestType === "TRANSCODED" ? `&isNotIngestType=${listCondition?.isNotIngestType}` : "";

        const keywordParam =
            compareVersions(stage.version, VERSION_CM_13540_ASSET_SEARCH_BAR_V2) >= 0 ? "keyword" : "q";
        const basePath = menuService.paths.integratedSearch(spaceId, projectId);
        const queryString = `?${keywordParam}=${encodeURIComponent(
            searchKeyword,
        )}&page=0&size=20${isNotIngestTypeParam}`;

        history.push(`${basePath}${queryString}`);
    };

    const openComments = () => {
        const type = CONSTANTS("RIGHT_SIDE_BAR_TYPES").COMMENT;
        if (!rightSideBar.active) {
            dispatch(toggleActive({ type }));
        }
    };

    const menuServicePaths = useMemo(() => (isLibrary ? menuService.paths.library : menuService.paths), [isLibrary]);

    useEffect(() => {
        if (!user?.stages?.data || !projectId) return;

        const activeStage = user.stages.data.filter((v) => v.id === spaceId)[0];
        if (activeStage) {
            const activeProject = activeStage.projects
                ? activeStage.projects.filter((v) => v.id === projectId)[0]
                : null;
        }
    }, [user, projectId]);

    useEffect(() => {
        if (!project.id) return;
        getSettingData("Asset");
    }, [project]);

    useEffect(() => {
        if (!settings?.project?.General) return;

        const { threadId, domain } = queryParamsParse(search);
        const isLinkFromEmail = !!threadId;
        const showComments =
            isLinkFromEmail && domain === "PROJECT" && !domainStatus.COMMENTS_OFF && COMMENTS_PERMISSIONS.READ;

        if (showComments) {
            openComments();
        }
    }, [location, domainStatus.COMMENTS_OFF, COMMENTS_PERMISSIONS.READ, settings?.project?.General]);

    return (
        <AppBar
            position="fixed"
            sx={{
                backgroundColor:
                    !isLibrary && headerTheme?.color ? headerTheme.color?.bgColor : theme.palette.background.default,
                color: !isLibrary && headerTheme?.color ? headerTheme.color?.textColor : theme.palette.text.primary,
                fill: !isLibrary && headerTheme?.color ? headerTheme.color?.bgColor : theme.palette.background.default,
                boxShadow: `none`,
                borderBottom: `1px solid ${alpha(theme.palette.greySecondary[300], 0.32)}`,
                zIndex: (props) => props.zIndex.drawer + 1,
            }}
        >
            <Toolbar sx={{ padding: `0 !important` }}>
                <Grid container justifyContent={`space-between`} alignItems={`center`}>
                    <Grid item>
                        <Grid container alignItems={"center"} height={theme.mixins.toolbar.minHeight}>
                            <Grid item px={1.5} hidden={isLibrary}>
                                <StartMenu headerTheme={headerTheme} />
                            </Grid>
                            <Divider
                                flexItem
                                orientation={"vertical"}
                                sx={{
                                    marginLeft: `-1px`,
                                    background: `none`,
                                    borderColor: alpha(theme.palette.greySecondary[300], 0.32),
                                }}
                            />
                            <Grid item pl={2.5}>
                                <Grid container alignItems={"center"}>
                                    <Logo
                                        to={isLibrary ? "/library/" : "/"}
                                        imgSrc={logoSrc}
                                        fill={
                                            isLibrary
                                                ? theme.palette.text.primary
                                                : headerTheme?.color?.textColor ?? theme.palette.text.primary
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container gap={1} pr={1} alignItems={"center"} height={theme.mixins.toolbar.minHeight}>
                            {!isLibrary && stage?.extensions?.["SEARCH_ENGINE_ENABLED"] === "true" && (
                                <>
                                    <Grid item sx={{ position: `relative`, display: "flex", flexDirection: "row" }}>
                                        <Tooltip title={t(`common::label::Search`, `Search`)}>
                                            <IconButton
                                                circled
                                                size={"large"}
                                                onClick={() => {
                                                    setIsSearch(true);
                                                    setSearchKeyword(``);
                                                    // setTimeout(() => searchRef && searchRef.current.focus(), 100);
                                                }}
                                                disabled={!INTEGRATED_SEARCH_PERMISSIONS.READ}
                                            >
                                                <IconSearch
                                                    size={20}
                                                    htmlColor={
                                                        isLibrary
                                                            ? theme.palette.greySecondary.main
                                                            : headerTheme?.color?.textColor ??
                                                              theme.palette.greySecondary.main
                                                    }
                                                    sx={{ opacity: 0.48 }}
                                                />
                                            </IconButton>
                                        </Tooltip>

                                        <Collapse
                                            orientation="horizontal"
                                            in={isSearch}
                                            sx={{
                                                position: `absolute`,
                                                right: 0,
                                                top: 0,
                                                bottom: 0,
                                                backgroundColor: theme.palette.greySecondary[50],
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Grid container flexWrap={"nowrap"} alignItems={"center"}>
                                                <form
                                                    onSubmit={(e) => {
                                                        e.preventDefault();
                                                        onSubmit();
                                                    }}
                                                >
                                                    <TextField
                                                        variant={"filled"}
                                                        type="search"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    {/*<IconButton*/}
                                                                    {/*    circled*/}
                                                                    {/*    onClick={(e) => {*/}
                                                                    {/*        setSearchKeyword(``);*/}
                                                                    {/*    }}*/}
                                                                    {/*>*/}
                                                                    {/*    <IconCancelCircle*/}
                                                                    {/*        size={12}*/}
                                                                    {/*        htmlColor={theme.palette.secondary[500]}*/}
                                                                    {/*    />*/}
                                                                    {/*</IconButton>*/}
                                                                    <Tooltip
                                                                        title={t(
                                                                            `common::label::Go to Search Results`,
                                                                            `Go to Search Results`,
                                                                        )}
                                                                    >
                                                                        <span>
                                                                            <IconButton
                                                                                circled
                                                                                size={"medium"}
                                                                                onClick={() => {
                                                                                    setIsSearch(false);
                                                                                    setSearchKeyword(``);
                                                                                    onSubmit();
                                                                                }}
                                                                            >
                                                                                <IconForward
                                                                                    size={16}
                                                                                    htmlColor={
                                                                                        theme.palette.greySecondary.main
                                                                                    }
                                                                                />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                    <Divider
                                                                        flexItem
                                                                        orientation={"vertical"}
                                                                        sx={{
                                                                            height: 24,
                                                                            alignSelf: `center`,
                                                                            mx: 1,
                                                                            borderColor: alpha(
                                                                                theme.palette.greySecondary[300],
                                                                                0.32,
                                                                            ),
                                                                        }}
                                                                    />
                                                                    <Tooltip title={t(`common::label::Close`, `Close`)}>
                                                                        <span>
                                                                            <IconButton
                                                                                circled
                                                                                size={"small"}
                                                                                onClick={() => {
                                                                                    setIsSearch(false);
                                                                                    setSearchKeyword(``);
                                                                                }}
                                                                            >
                                                                                <IconClose
                                                                                    size={12}
                                                                                    htmlColor={
                                                                                        theme.palette.greySecondary.main
                                                                                    }
                                                                                />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                </InputAdornment>
                                                            ),
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconSearch size={16} color={"greySecondary"} />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        fullWidth
                                                        value={searchKeyword}
                                                        onChange={(e) => {
                                                            setSearchKeyword(e.target.value);
                                                        }}
                                                        placeholder={t(
                                                            `common::label::Search for ${
                                                                compareVersions(
                                                                    stage.version,
                                                                    VERSION_CM_13540_ASSET_SEARCH_BAR_V2,
                                                                ) < 0
                                                                    ? "storage and "
                                                                    : ""
                                                            }assets by all projects.`,
                                                        )}
                                                        sx={{
                                                            minWidth: 400,
                                                            ".MuiInputBase-root": {
                                                                backgroundColor: `transparent`,
                                                                paddingRight: 0,
                                                                "&:has(input[type='search']):hover , &:has(input[type='search']):focus, &:has(input[type='search']).Mui-focused":
                                                                    {
                                                                        backgroundColor: `transparent`,
                                                                    },
                                                            },
                                                        }}
                                                    />
                                                </form>
                                            </Grid>
                                        </Collapse>
                                    </Grid>

                                    <Divider
                                        flexItem
                                        orientation={"vertical"}
                                        sx={{
                                            my: "18px",
                                            background: `none`,
                                            borderColor: alpha(theme.palette.secondary[500], 0.2),
                                        }}
                                    />
                                </>
                            )}
                            {compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0 && (
                                <>
                                    <Button
                                        startIcon={<IconShared size={16} />}
                                        variant={"text"}
                                        rounded
                                        color={location.pathname.includes("share") ? "primary" : "greySecondary"}
                                        size={"small"}
                                        sx={{
                                            backgroundColor: location.pathname.includes("share")
                                                ? alpha(theme.palette.primary.main, 0.12)
                                                : "secondary",
                                        }}
                                        onClick={() => {
                                            history.push(menuServicePaths.share(spaceId, projectId));
                                        }}
                                    >
                                        <Link
                                            to={`${menuServicePaths.share(spaceId, projectId)}`}
                                            style={{ textDecoration: "none" }}
                                        >
                                            {t("common::label::Share", "Share")}
                                        </Link>
                                    </Button>
                                </>
                            )}
                            {compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && (
                                <Button
                                    startIcon={<IconConsoleRequest size={16} />}
                                    variant={"text"}
                                    rounded
                                    color={location.pathname.includes("requests") ? "primary" : "greySecondary"}
                                    size={"small"}
                                    sx={{
                                        backgroundColor: location.pathname.includes("requests")
                                            ? alpha(theme.palette.primary.main, 0.12)
                                            : "secondary",
                                    }}
                                    onClick={() => {
                                        history.push(menuServicePaths.requests(spaceId, projectId));
                                    }}
                                >
                                    <Link
                                        to={`${menuServicePaths.requests(spaceId, projectId)}`}
                                        style={{ textDecoration: "none" }}
                                    >
                                        {t("common::label::Request", "Request")}
                                    </Link>
                                </Button>
                            )}
                            {(isLocal || isDev || isQA) && (
                                <Grid item>
                                    <SelectStage />
                                </Grid>
                            )}
                            <VersionedComponent version={VERSION_CM_15524_NOTIFY_POPPER}>
                                <Grid item>
                                    <NotifyButton />
                                </Grid>
                            </VersionedComponent>
                            {!domainStatus.COMMENTS_OFF && (
                                <Grid item>
                                    <Tooltip title={t(`common::label::Comment`, `Comment`)}>
                                        <span>
                                            <IconButton
                                                size={"large"}
                                                circled
                                                sx={{
                                                    p: 1,
                                                    backgroundColor:
                                                        rightSideBar.active &&
                                                        rightSideBar.type === CONSTANTS("RIGHT_SIDE_BAR_TYPES").COMMENT
                                                            ? alpha(theme.palette.common.black, 0.08)
                                                            : `transparent`,
                                                    "& div svg": { marginBottom: "8px" },
                                                }}
                                                id={SIDEBAR_BUTTON_ID}
                                                onClick={(e) => {
                                                    // e.stopPropagation();
                                                    openComments();
                                                }}
                                                disabled={!COMMENTS_PERMISSIONS.READ}
                                            >
                                                <IconReply
                                                    size={20}
                                                    htmlColor={
                                                        isLibrary
                                                            ? theme.palette.greySecondary.main
                                                            : headerTheme?.color?.textColor ??
                                                              theme.palette.greySecondary.main
                                                    }
                                                    sx={{ opacity: 0.48 }}
                                                />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            )}
                            <Divider
                                flexItem
                                orientation={"vertical"}
                                sx={{
                                    my: "18px",
                                    background: `none`,
                                    borderColor: alpha(theme.palette.greySecondary[300], 0.32),
                                }}
                            />
                            <Grid item>
                                <MyJobsButton />
                            </Grid>
                            <Grid item>
                                <ProfileMenu headerTheme={headerTheme} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Toolbar>
            {activeAppDownModal &&
                ReactDOM.createPortal(<LoadableModelForAppDown onClose={closeAppDownModal} />, document.body)}
        </AppBar>
    );
};

export default HeaderForLayout;
